import { Injectable, inject, signal } from '@angular/core';
import type { Observable } from 'rxjs';
import { forkJoin, map, of, tap } from 'rxjs';

import type { LookupCategoryResponse, LookupOption } from '../models/lookup.model';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class LookupService {
  private readonly api = inject(ApiService);

  private readonly cache = signal<ReadonlyMap<string, LookupOption[]>>(new Map());

  /** Returns cached options or fetches and caches them. */
  getOptions(category: string): Observable<LookupOption[]> {
    const cached = this.cache().get(category);

    if (cached !== undefined) {
      return of([...cached]);
    }

    return this.fetchAndCache(category);
  }

  loadCategory(category: string): Observable<LookupOption[]> {
    return this.fetchAndCache(category);
  }

  preload(categories: string[]): Observable<void> {
    const uncached = categories.filter((code) => !this.cache().has(code));

    if (uncached.length === 0) {
      return of(undefined);
    }

    return forkJoin(uncached.map((code) => this.fetchAndCache(code))).pipe(map(() => undefined));
  }

  getCached(category: string): LookupOption[] | undefined {
    return this.cache().get(category);
  }

  resolveLabel(category: string, value: string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const option = this.cache().get(category)?.find((row) => row.value === value);

    return option?.label ?? value;
  }

  resolveMeta(
    category: string,
    value: string | null | undefined,
  ): Record<string, unknown> | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return this.cache().get(category)?.find((row) => row.value === value)?.meta ?? null;
  }

  invalidate(category?: string): void {
    if (category === undefined) {
      this.cache.set(new Map());
      return;
    }

    const next = new Map(this.cache());
    next.delete(category);
    this.cache.set(next);
  }

  private fetchAndCache(category: string): Observable<LookupOption[]> {
    return this.api.get<LookupCategoryResponse>(`lookups/${category}`).pipe(
      tap((response) => this.store(category, response.options)),
      map((response) => response.options),
    );
  }

  private store(category: string, options: LookupOption[]): void {
    const next = new Map(this.cache());
    next.set(category, options);
    this.cache.set(next);
  }
}
